import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './CookieConsent.css';

const STORAGE_KEY = 'reff-cookie-consent';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const DEFAULT_PREFERENCES: CookiePreferences = {
  necessary: true,
  analytics: true,
  marketing: true,
};

function getStoredConsent(): CookiePreferences | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CookiePreferences;
  } catch {
    return null;
  }
}

function storeConsent(prefs: CookiePreferences) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* private mode */
  }
}

const CookieConsent: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [prefs, setPrefs] = useState<CookiePreferences>(DEFAULT_PREFERENCES);
  const [keepPosted, setKeepPosted] = useState(false);

  useEffect(() => {
    const stored = getStoredConsent();
    if (!stored) {
      // Show banner after a short delay so it doesn't flash on load
      const timer = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(timer);
    }
    // Already consented; don't show
    return undefined;
  }, []);

  const handleAcceptAll = useCallback(() => {
    const all: CookiePreferences = { necessary: true, analytics: true, marketing: true };
    storeConsent(all);
    setVisible(false);
    setManageOpen(false);
  }, []);

  const handleRejectAll = useCallback(() => {
    const minimal: CookiePreferences = { necessary: true, analytics: false, marketing: false };
    storeConsent(minimal);
    setVisible(false);
    setManageOpen(false);
  }, []);

  const handleSavePreferences = useCallback(() => {
    storeConsent(prefs);
    setVisible(false);
    setManageOpen(false);
  }, [prefs]);

  if (!visible) return null;

  return (
    <div className="cc-wrapper" role="dialog" aria-label="Cookie consent" aria-modal={manageOpen}>
      {/* Backdrop when manage panel is open */}
      {manageOpen && <div className="cc-backdrop" onClick={() => setManageOpen(false)} />}

      <div className={`cc-card ${manageOpen ? 'cc-card--manage' : ''}`}>
        {/* Header */}
        <div className="cc-header">
          <span className="cc-cookie-icon" aria-hidden>🍪</span>
          <div className="cc-header-text">
            <h3 className="cc-title">We value your privacy</h3>
            <p className="cc-desc">
              We use cookies to run the site, analyse traffic, and improve your experience. Choose which
              cookies to allow. See our{' '}
              <Link to="/cookie-policy" className="cc-link">Cookie Policy</Link>.
            </p>
          </div>
        </div>

        {/* Manage panel (expanded) */}
        {manageOpen && (
          <div className="cc-manage">
            <div className="cc-category">
              <div className="cc-category-info">
                <span className="cc-category-title">Necessary</span>
                <span className="cc-category-desc">Required for the site to work. Always on.</span>
              </div>
              <input
                type="checkbox"
                className="cc-toggle"
                checked
                disabled
                aria-label="Necessary cookies (always on)"
              />
            </div>

            <div className="cc-category">
              <div className="cc-category-info">
                <span className="cc-category-title">Analytics</span>
                <span className="cc-category-desc">Helps us understand how the site is used (Google Analytics).</span>
              </div>
              <input
                type="checkbox"
                className="cc-toggle"
                checked={prefs.analytics}
                onChange={(e) => setPrefs((p) => ({ ...p, analytics: e.target.checked }))}
                aria-label="Analytics cookies"
              />
            </div>

            <div className="cc-category">
              <div className="cc-category-info">
                <span className="cc-category-title">Marketing</span>
                <span className="cc-category-desc">Used to personalise content and measure campaigns.</span>
              </div>
              <input
                type="checkbox"
                className="cc-toggle"
                checked={prefs.marketing}
                onChange={(e) => setPrefs((p) => ({ ...p, marketing: e.target.checked }))}
                aria-label="Marketing cookies"
              />
            </div>

            <label className="cc-keep-posted">
              <input
                type="checkbox"
                className="cc-checkbox"
                checked={keepPosted}
                onChange={(e) => setKeepPosted(e.target.checked)}
              />
              <span>Keep me posted on Reffconnect product updates</span>
            </label>
          </div>
        )}

        {/* Actions */}
        <div className="cc-actions">
          {!manageOpen ? (
            <>
              <button type="button" className="cc-btn cc-btn--outline" onClick={() => setManageOpen(true)}>
                Manage
              </button>
              <button type="button" className="cc-btn cc-btn--outline" onClick={handleRejectAll}>
                Reject all
              </button>
              <button type="button" className="cc-btn cc-btn--primary" onClick={handleAcceptAll}>
                Accept all
              </button>
            </>
          ) : (
            <>
              <button type="button" className="cc-btn cc-btn--outline" onClick={handleRejectAll}>
                Reject all
              </button>
              <button type="button" className="cc-btn cc-btn--primary" onClick={handleSavePreferences}>
                Save preferences
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
